import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, where, orderBy, getDoc, runTransaction, increment } from 'firebase/firestore';
import { db, auth } from '@municipality/firebase';
import { invitationService } from '@municipality/firebase';

// Municipality Status Type Definition
export type MunicipalityStatus = 'trial' | 'provisional' | 'active';

// Type guard to validate status
export const isValidMunicipalityStatus = (status: unknown): status is MunicipalityStatus => {
  return typeof status === 'string' && ['trial', 'provisional', 'active'].includes(status);
};

// Validation function with error throwing
export const validateMunicipalityStatus = (status: unknown): MunicipalityStatus => {
  if (!isValidMunicipalityStatus(status)) {
    throw new Error(`Invalid municipality status: ${status}. Must be one of: trial, provisional, active`);
  }
  return status;
};

export interface Municipality {
  id: string;
  displayId?: number;
  name: string;
  email: string;
  country: string;
  phone?: string;
  status: MunicipalityStatus;
  createdAt: string;
  updatedAt: string;
  invitationSentAt?: string;
  invitationToken?: string;
  // Removed: adminName, adminId (user data should not be in municipality)
}

export interface CreateMunicipalityData {
  name: string;
  email: string;
  country: string;
  phone?: string;
  adminFirstName: string;
  adminLastName: string;
  status?: MunicipalityStatus; // Optional, defaults to 'trial'
}

export interface UpdateMunicipalityData {
  name?: string;
  email?: string;
  country?: string;
  phone?: string;
  status?: MunicipalityStatus;
  invitationSentAt?: string;
  invitationToken?: string;
  // Removed: adminName, adminId (user data should not be in municipality)
}

/** Throttle window for getMunicipalities (ms) - dedupe concurrent and near-simultaneous calls */
const GET_MUNICIPALITIES_CACHE_MS = 2000;

class MunicipalitiesService {
  private collectionName = 'municipalities';
  private getMunicipalitiesInFlight: Promise<Municipality[]> | null = null;
  private getMunicipalitiesCache: { data: Municipality[]; until: number } | null = null;

  private invalidateMunicipalitiesCache(): void {
    this.getMunicipalitiesCache = null;
  }

  // Get the counter document reference for atomic displayId generation
  private getCounterDoc() {
    return doc(db, 'municipalities', '_counter');
  }
  
  /**
   * Get the next sequential displayId atomically (prevents race conditions)
   */
  private async getNextDisplayId(): Promise<number> {
    try {
      const counterRef = this.getCounterDoc();
      
      // Query all municipalities to find the max displayId (excluding _counter document)
      // We need to do this to ensure counter is always >= max displayId
      const municipalitiesRef = collection(db, this.collectionName);
      const municipalitiesSnapshot = await getDocs(municipalitiesRef);
      
      let maxDisplayId = 0;
      municipalitiesSnapshot.forEach((doc) => {
        // Skip the _counter document
        if (doc.id === '_counter') {
          return;
        }
        const data = doc.data();
        if (data.displayId && typeof data.displayId === 'number' && data.displayId > maxDisplayId) {
          maxDisplayId = data.displayId;
        }
      });
      
      console.log('🏢 MunicipalitiesService: Max displayId found in database:', maxDisplayId);
      
      // Now use transaction to atomically get/increment counter
      return await runTransaction(db, async (transaction) => {
        const counterDocInTransaction = await transaction.get(counterRef);
        
        if (!counterDocInTransaction.exists()) {
          // Counter doesn't exist - initialize it
          const nextDisplayId = maxDisplayId + 1;
          transaction.set(counterRef, { count: nextDisplayId });
          console.log('🏢 MunicipalitiesService: Initialized counter with maxDisplayId:', maxDisplayId, 'nextDisplayId:', nextDisplayId);
          return nextDisplayId;
        } else {
          // Counter exists - check if it's out of sync
          const currentCount = counterDocInTransaction.data()?.count || 0;
          
          // Safety check: if counter is less than max displayId, reset it
          if (currentCount <= maxDisplayId) {
            const nextDisplayId = maxDisplayId + 1;
            console.log('🏢 MunicipalitiesService: Counter out of sync! Current:', currentCount, 'Max:', maxDisplayId, 'Resetting to:', nextDisplayId);
            transaction.set(counterRef, { count: nextDisplayId });
            return nextDisplayId;
          }
          
          // Counter is valid - increment atomically
          transaction.update(counterRef, { count: increment(1) });
          const nextDisplayId = currentCount + 1;
          console.log('🏢 MunicipalitiesService: Incrementing counter from', currentCount, 'to', nextDisplayId);
          return nextDisplayId;
        }
      });
    } catch (error) {
      console.error('🏢 MunicipalitiesService: Error getting next displayId:', error);
      throw new Error('Failed to generate displayId');
    }
  }

  public async getMunicipalities(): Promise<Municipality[]> {
    console.log('🏢 MunicipalitiesService: getMunicipalities called');
    const now = Date.now();
    if (this.getMunicipalitiesCache && this.getMunicipalitiesCache.until > now) {
      console.log('🏢 MunicipalitiesService: Returning cached result (dedupe)');
      return this.getMunicipalitiesCache.data;
    }
    if (this.getMunicipalitiesInFlight) {
      console.log('🏢 MunicipalitiesService: Request in flight, awaiting same promise (dedupe)');
      return this.getMunicipalitiesInFlight;
    }
    const promise = this.fetchMunicipalitiesFromFirestore();
    this.getMunicipalitiesInFlight = promise;
    try {
      const data = await promise;
      this.getMunicipalitiesCache = { data, until: now + GET_MUNICIPALITIES_CACHE_MS };
      return data;
    } finally {
      this.getMunicipalitiesInFlight = null;
    }
  }

  private async fetchMunicipalitiesFromFirestore(): Promise<Municipality[]> {
    try {
      console.log('🏢 MunicipalitiesService: About to query Firestore collection:', this.collectionName);
      const q = query(
        collection(db, this.collectionName),
        orderBy('createdAt', 'desc')
      );
      
      console.log('🏢 MunicipalitiesService: Executing Firestore query');
      const querySnapshot = await getDocs(q);
      console.log('🏢 MunicipalitiesService: Query completed, documents found:', querySnapshot.size);
      
      const municipalities: Municipality[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        console.log('🏢 MunicipalitiesService: Processing document:', doc.id, data);
        municipalities.push({
          id: doc.id,
          displayId: data.displayId,
          name: data.name,
          email: data.email,
          country: data.country,
          phone: data.phone,
          status: isValidMunicipalityStatus(data.status) ? data.status : 'trial',
          createdAt: data.createdAt,
          updatedAt: data.updatedAt,
          invitationSentAt: data.invitationSentAt,
          invitationToken: data.invitationToken,
        });
      });
      
      console.log('🏢 MunicipalitiesService: Returning municipalities:', municipalities);
      return municipalities;
    } catch (error: unknown) {
      console.error('🏢 MunicipalitiesService: Error fetching municipalities:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch municipalities';
      throw new Error(errorMessage);
    }
  }

  public async getMunicipalityById(id: string): Promise<Municipality | null> {
    try {
      const docRef = doc(db, this.collectionName, id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          displayId: data.displayId,
          name: data.name,
          email: data.email,
          country: data.country,
          phone: data.phone,
          status: isValidMunicipalityStatus(data.status) ? data.status : 'trial',
          createdAt: data.createdAt,
          updatedAt: data.updatedAt,
          invitationSentAt: data.invitationSentAt,
          invitationToken: data.invitationToken,
        };
      }
      
      return null;
    } catch (error: unknown) {
      console.error('Error fetching municipality:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch municipality';
      throw new Error(errorMessage);
    }
  }

  public async createMunicipality(data: CreateMunicipalityData): Promise<Municipality> {
    console.log('🏢 MunicipalitiesService: createMunicipality called with:', {
      name: data.name,
      email: data.email,
      country: data.country,
      adminFirstName: data.adminFirstName,
      adminLastName: data.adminLastName
    });

    try {
      const now = new Date().toISOString();
      
      // Get next sequential displayId atomically (prevents race conditions)
      console.log('🏢 MunicipalitiesService: Getting next displayId atomically...');
      const nextDisplayId = await this.getNextDisplayId();
      console.log('🏢 MunicipalitiesService: Got next displayId:', nextDisplayId);
      
      // Create clean municipality record - NO user data (no adminName, no adminId)
      const municipalityData: Record<string, unknown> = { 
        displayId: nextDisplayId,
        name: data.name,
        email: data.email,
        country: data.country,
        status: data.status || 'trial' as const,
        createdAt: now,
        updatedAt: now,
      };
      
      // Only add phone if it has a value (Firestore doesn't allow undefined)
      if (data.phone) {
        municipalityData.phone = data.phone;
      }
      
      console.log('🏢 MunicipalitiesService: Saving municipality to Firestore...');
      const docRef = await addDoc(collection(db, this.collectionName), municipalityData);
      console.log('✅ MunicipalitiesService: Municipality saved to Firestore with ID:', docRef.id);
      
      // Create invitation and send email using the proper service
      if (data.email) {
        console.log('🏢 MunicipalitiesService: Creating invitation for email:', data.email);
        try {
          // Get current user (super admin) who is sending the invitation
          const currentUser = auth.currentUser;
          const invitedByName = currentUser?.displayName || currentUser?.email || 'Super Administrator';
          
          // Pass firstName and lastName separately to preserve data
          // adminName = the person who sent the invitation (super admin)
          // firstName/lastName = the person being invited (municipality admin)
          await invitationService.createInvitation(
            data.email,
            data.name,
            docRef.id,
            'municipality_admin', // Canonical lowercase
            invitedByName, // adminName = super admin who sent the invitation
            data.country,
            data.phone,
            'admin', // municipalityRole
            data.adminFirstName, // firstName = invitee's first name
            data.adminLastName   // lastName = invitee's last name
          );
          console.log('✅ MunicipalitiesService: Invitation email sent successfully to:', data.email);
        } catch (emailError: unknown) {
          console.error('❌ MunicipalitiesService: Failed to send invitation email:', {
            error: emailError,
            message: emailError instanceof Error ? emailError.message : 'Unknown error',
            email: data.email
          });
          // Don't fail the entire operation if email fails
        }
      } else {
        console.warn('⚠️ MunicipalitiesService: No email provided, skipping invitation creation');
      }
      
      // Build result object with proper typing
      const result: Municipality = {
        id: docRef.id,
        name: data.name,
        email: data.email,
        country: data.country,
        phone: data.phone,
        status: data.status || 'trial',
        createdAt: municipalityData.createdAt as string,
        updatedAt: municipalityData.updatedAt as string,
      };

      console.log('✅ MunicipalitiesService: Municipality creation completed successfully:', {
        id: result.id,
        name: result.name,
        email: result.email,
        status: result.status
      });

      this.invalidateMunicipalitiesCache();
      return result;
    } catch (error: unknown) {
      console.error('🏢 MunicipalitiesService: Error creating municipality:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to create municipality';
      throw new Error(errorMessage);
    }
  }

  public async updateMunicipality(id: string, data: UpdateMunicipalityData): Promise<void> {
    try {
      // Validate status if provided
      if (data.status !== undefined) {
        validateMunicipalityStatus(data.status);
      }
      
      const docRef = doc(db, this.collectionName, id);
      
      // Filter out undefined values (Firestore doesn't allow them)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const updateData: any = {
        updatedAt: new Date().toISOString(),
      };
      
      // Only add fields that have values
      if (data.name !== undefined) updateData.name = data.name;
      if (data.email !== undefined) updateData.email = data.email;
      if (data.country !== undefined) updateData.country = data.country;
      if (data.phone !== undefined) updateData.phone = data.phone;
      if (data.status !== undefined) updateData.status = data.status;
      if (data.invitationSentAt !== undefined) updateData.invitationSentAt = data.invitationSentAt;
      if (data.invitationToken !== undefined) updateData.invitationToken = data.invitationToken;
      
      await updateDoc(docRef, updateData);
      this.invalidateMunicipalitiesCache();
    } catch (error: unknown) {
      console.error('Error updating municipality:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to update municipality';
      throw new Error(errorMessage);
    }
  }

  public async deleteMunicipality(id: string): Promise<void> {
    try {
      const docRef = doc(db, this.collectionName, id);
      await deleteDoc(docRef);
      this.invalidateMunicipalitiesCache();
    } catch (error: unknown) {
      console.error('Error deleting municipality:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete municipality';
      throw new Error(errorMessage);
    }
  }

  public async sendInvitation(id: string): Promise<void> {
    // Note: This method is deprecated in favor of creating a new invitation via the form
    // where admin name details can be provided. Municipality records no longer store admin info.
    try {
      const municipality = await this.getMunicipalityById(id);
      if (!municipality) {
        throw new Error('Municipality not found');
      }

      if (!municipality.email) {
        throw new Error('Municipality has no email address');
      }

      // Since municipality no longer stores admin name, we can't send a personalized invitation
      // The UI should prompt for admin details via the invitation form instead
      await invitationService.createInvitation(
        municipality.email,
        municipality.name,
        municipality.id,
        'municipality_admin', // Canonical lowercase
        undefined, // adminName not stored in municipality
        municipality.country,
        municipality.phone,
        'admin' // municipalityRole
        // firstName and lastName not available without user input
      );

      // Update municipality with invitation data
      const docRef = doc(db, this.collectionName, id);
      await updateDoc(docRef, {
        invitationSentAt: new Date().toISOString(),
        invitationToken: this.generateInvitationToken(),
      });
      this.invalidateMunicipalitiesCache();
    } catch (error: unknown) {
      console.error('Error sending invitation:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to send invitation';
      throw new Error(errorMessage);
    }
  }

  public async getMunicipalitiesByStatus(status: Municipality['status']): Promise<Municipality[]> {
    try {
      const q = query(
        collection(db, this.collectionName),
        where('status', '==', status)
      );
      
      const querySnapshot = await getDocs(q);
      const municipalities: Municipality[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        municipalities.push({
          id: doc.id,
          name: data.name,
          email: data.email,
          country: data.country,
          phone: data.phone,
          status: isValidMunicipalityStatus(data.status) ? data.status : 'trial',
          createdAt: data.createdAt,
          updatedAt: data.updatedAt,
          invitationSentAt: data.invitationSentAt,
          invitationToken: data.invitationToken,
        });
      });
      
      return municipalities;
    } catch (error: unknown) {
      console.error('Error fetching municipalities by status:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch municipalities by status';
      throw new Error(errorMessage);
    }
  }

  private generateInvitationToken(): string {
    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
  }
}

export const municipalitiesService = new MunicipalitiesService(); 